import { useMemo, useState } from 'react';
import { BroadcastIcon, ClockIcon, GaugeIcon, LinkIcon, MapPinIcon } from '@phosphor-icons/react';

import classNames from '@utils/classnames';
import { Button } from 'components/button/Button';
import { Card } from 'components/Card';
import { DataField } from 'components/DataField';
import { ErrorState } from 'components/error-state/ErrorState';
import { TrailerMap, type TrailerMapMarker } from 'components/map/TrailerMap';
import { Pill } from 'components/pill/Pill';
import { SpinnerBlock } from 'components/Spinner';
import { useTranslation } from 'contexts/translation-context';
import { getCountryFlag } from '@utils/country-flag';
import type { TrailerOption } from 'src/app/trailer/displayTrailer';

import { LIVE_TRACKING_REFRESH_MS, useTrailerTracking, type TelemetryParty } from '../models';
import { statusPill } from '../status';
import { LinkTrackerDialog } from './LinkTrackerDialog';

interface ITrailerTrackingPanelProps {
  /** The trailer this panel is about: its id is what is linked, and what the picker offers to change. */
  trailer: TrailerOption;
  party?: TelemetryParty;
  /** Panel title. Defaults to a generic "Tracking"; a panel opened under a list names the trailer. */
  title?: string;
  /** Whether the reader may re-link this trailer from here — a driver may only look. */
  canLink?: boolean;
  /**
   * The caller's own data has changed with this link — the rows it shows about the unit that was moved, say
   * — so it can refresh what it shows about it.
   */
  onLinked?: () => void;
  /**
   * Whether the reader can change this panel's width — a side panel beside a list rather than a fixed
   * column. Then the map steps up in height as the panel is widened, instead of stretching into an ever
   * wider letterbox. Left out, the map keeps one height wherever the panel lands.
   */
  resizableWidth?: boolean;
}

/**
 * A trailer's live position and the tracker behind it.
 *
 * The server refreshes the connection's snapshot when the page asks for it, so mounting this panel is what
 * fetches a position — once. It repeats on an interval only while live tracking is on, because the
 * credentials belong to the account and an idle page must not spend their API quota.
 *
 * Linking is the one thing this panel does that the truck's does not. A truck's tracker is linked from the
 * tracking settings; a trailer's belongs on the trailer, where the user who installed it already is.
 */
export const TrailerTrackingPanel = ({
  trailer,
  party,
  title,
  canLink = true,
  onLinked,
  resizableWidth = false,
}: ITrailerTrackingPanelProps) => {
  const { i18n, lang } = useTranslation();
  const [isLive, setIsLive] = useState(false);
  const [isLinking, setIsLinking] = useState(false);
  const { data, error, isLoading, mutate } = useTrailerTracking(
    trailer.id,
    party,
    isLive ? LIVE_TRACKING_REFRESH_MS : 0,
  );

  const position = data?.position ?? null;
  const pill = statusPill(position?.status);

  // Keyed on the position's values rather than on the object: a poll that reports the same place must not
  // change the marker's identity, or the map re-frames itself — over wherever the user had panned to.
  const markers = useMemo<TrailerMapMarker[]>(
    () =>
      position
        ? [
            {
              id: trailer.id,
              label: trailer.licensePlate,
              latitude: position.latitude,
              longitude: position.longitude,
              status: position.status,
            },
          ]
        : [],
    [trailer.id, trailer.licensePlate, position?.latitude, position?.longitude, position?.status],
  );

  const formatMoment = (value: string | null | undefined): string => {
    if (!value) {
      return '—';
    }
    return new Date(value).toLocaleString(lang, { dateStyle: 'short', timeStyle: 'short' });
  };

  const panelTitle = title ?? i18n('liveTracking.title');

  if (isLoading && !data) {
    return (
      <Card title={panelTitle}>
        <SpinnerBlock message={i18n('liveTracking.loading')} className="min-h-32" />
      </Card>
    );
  }

  if (error && !data) {
    return (
      <Card title={panelTitle}>
        <ErrorState error={error} onRetry={() => mutate()} />
      </Card>
    );
  }

  const place = [data?.place?.city, data?.place?.country].filter(Boolean).join(', ');

  // Nothing came back: name the reason rather than always blaming the link. A connection that is in error —
  // a rejected key, say — is not the same problem as a trailer nobody has linked yet.
  const emptyText = !data?.provider
    ? i18n('liveTracking.noTrackerLinked')
    : (data.connectionError ?? i18n('liveTracking.noTrailerPosition'));

  return (
    <Card
      title={panelTitle}
      subtitle={i18n('liveTracking.trailerSubtitle')}
      rightHeading={
        <div className="flex flex-wrap items-center gap-2">
          {data?.provider && (
            <Pill color="primary" size="sm">
              {data.connectionLabel ?? data.provider}
            </Pill>
          )}
          {pill && (
            <Pill color={pill.color} size="sm">
              {i18n(pill.labelKey)}
            </Pill>
          )}
          {/* Always offered, not only when nothing is linked: a tracker that moved to another trailer is
              the same button as one that was never there. */}
          {canLink && (
            <Button
              color="secondary"
              size={8}
              iconLeft={<LinkIcon className="button-icon" />}
              onTrigger={() => setIsLinking(true)}
            >
              {i18n('liveTracking.linkTracker')}
            </Button>
          )}
        </div>
      }
    >
      <div className="@container grid gap-4">
        {position ? (
          <>
            {/* The height steps key on this grid's own width, which is the panel's — so a panel the reader
                drags wider gets a taller map rather than a wider strip of the same height. */}
            <TrailerMap
              markers={markers}
              className={classNames(
                'w-full rounded-lg overflow-hidden',
                resizableWidth ? 'h-72 @lg:h-80 @2xl:h-96' : 'h-72',
              )}
            />

            <div className="grid grid-cols-2 gap-4 @md:grid-cols-4">
              <DataField title={i18n('liveTracking.location')}>
                {getCountryFlag(data?.place?.country)} {place || '—'}
              </DataField>
              <DataField title={i18n('liveTracking.speed')}>
                {position.speed === null ? '—' : `${position.speed} km/u`}
              </DataField>
              <DataField title={i18n('liveTracking.heading')}>
                {position.heading === null ? '—' : `${position.heading}°`}
              </DataField>
              <DataField title={i18n('liveTracking.lastMeasurement')}>{formatMoment(position.recordedAt)}</DataField>
            </div>
          </>
        ) : (
          <div className="flex items-center gap-2 text-sm text-fg-muted">
            <MapPinIcon className="w-5 h-5 shrink-0" />
            <span>{emptyText}</span>
          </div>
        )}

        {/* Kept out of the branch above on purpose: retrying and turning live tracking on is exactly what a
            user wants when nothing has come back yet, so neither control may hide behind a position. */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-sm text-fg-muted">
            {isLive ? (
              <>
                <BroadcastIcon className="w-5 h-5 shrink-0 text-positive-text" weight="fill" />
                <span>{i18n('liveTracking.liveActive')}</span>
              </>
            ) : data?.lastSeenAt ? (
              <>
                <ClockIcon className="w-5 h-5 shrink-0" />
                {/* The panel knows when the trailer last reported, not when it was fetched — so say that. */}
                <span>
                  {i18n('liveTracking.lastSignal')}: {formatMoment(data.lastSeenAt)}
                </span>
              </>
            ) : null}
          </div>

          <div className="flex items-center gap-2">
            <Button color="ghost" onTrigger={() => mutate()} iconLeft={<GaugeIcon className="button-icon" />}>
              {i18n('liveTracking.refresh')}
            </Button>
            <Button
              color={isLive ? 'secondary' : 'primary'}
              onTrigger={() => setIsLive((previous) => !previous)}
              iconLeft={<BroadcastIcon className="button-icon" />}
            >
              {i18n(isLive ? 'liveTracking.stopLive' : 'liveTracking.startLive')}
            </Button>
          </div>
        </div>
      </div>

      {/* Mounted rather than toggled: the dialog fetches the fleet's units, and a trailer page that is only
          being read must not spend three provider calls on a picker nobody opened. */}
      {isLinking && (
        <LinkTrackerDialog
          trailer={trailer}
          onClose={() => setIsLinking(false)}
          onLinked={async () => {
            await mutate();
            onLinked?.();
          }}
        />
      )}
    </Card>
  );
};
